import type {
  GamePlayerProjectionTrading,
  ProbabilityType
} from '@library/types/trading';
import {
  Input,
  TradeSettingsProbabilityInput
} from '@library/components/inputs';
import {
  PlayerModalProjectionBoardTimePicker,
  PlayerModalProjectionStartTimePicker
} from '@library/components/modals/PlayerModal/StatTable';
import {
  ProjectionInputCell,
  RankInputCell,
  StatTypeLabel,
  TenGameAverage
} from '@library/components/modals/PlayerModal/NoContextPlayerModal/StatTable';
import { Select, StatusSelect } from '@library/components/selects';
import { isTimeAfter, isTimeBefore } from '@library/utils/dateTime';
import { useCallback, useEffect } from 'react';
import { DateTime } from 'luxon';
import { DisplayTimePicker } from '@library/components/simulations';
import type { SelectOption } from '@library/types/utils';
import type { Status } from '@library/types/projection';
import { determineScoreError } from '@library/utils';
import { onAutoTradeInGameChange } from '@bo-utils/projection';

import { useGetAverageScoreMutation } from '@bo-services/projectionsApi';
import { useSplitFlag } from '@bo-hooks/useSplitFlag';

export interface PlayerModalProjectionRowProps {
  change?: Partial<GamePlayerProjectionTrading>;
  onResetCreateProjections: () => void;
  projection: GamePlayerProjectionTrading;
  updateProjection: ({
    change,
    initialProjection
  }: {
    change: Partial<GamePlayerProjectionTrading>;
    initialProjection: GamePlayerProjectionTrading;
  }) => void;
  statTypes: Array<SelectOption>;
  isDisplayTimeVisible: boolean;
}

export const PlayerModalProjectionRow = ({
  change = {},
  onResetCreateProjections,
  projection,
  updateProjection,
  statTypes,
  isDisplayTimeVisible
}: PlayerModalProjectionRowProps) => {
  const displayProjection = {
    ...projection,
    ...change
  };

  const {
    autoTrade,
    averageScore,
    boardTime,
    dateError,
    description,
    draft,
    durationId,
    displayTime,
    foreignId,
    inGame,
    isCommunityPlay,
    isCopied,
    isPromo,
    maxProbabilityOfMore,
    minProbabilityOfMore,
    oddsType,
    optIn,
    playerId,
    projectionId,
    projectionValue,
    rank,
    scoreError,
    statType,
    startTime,
    statTypeId = '',
    status,
    targetProbabilityOfMore
  } = displayProjection;

  const onChangeBoardTime = (newBoardTime: DateTime) => {
    onResetCreateProjections();

    const newDateError = isTimeAfter({
      dateValue: newBoardTime,
      dateToCompareAgainst: DateTime.fromISO(startTime)
    });

    updateProjection({
      change: {
        boardTime: newBoardTime.toISO() ?? '',
        dateError: newDateError
      },
      initialProjection: projection
    });
  };

  const onChangeStartTime = (newStartTime: DateTime) => {
    onResetCreateProjections();

    updateProjection({
      change: {
        startTime: newStartTime.toISO() ?? ''
      },
      initialProjection: projection
    });
  };

  const onChangeDisplayTime = useCallback(
    (newDisplayTime: DateTime) => {
      onResetCreateProjections();

      updateProjection({
        change: {
          displayTime: newDisplayTime.toISO() ?? ''
        },
        initialProjection: projection
      });
    },
    [onResetCreateProjections, updateProjection, projection]
  );

  const onRankChange = (newRank?: number | null) => {
    onResetCreateProjections();

    updateProjection({
      change: {
        rank: newRank
      },
      initialProjection: projection
    });
  };

  const onDescriptionChange = (newDescription: string) => {
    onResetCreateProjections();

    updateProjection({
      change: {
        description: newDescription
      },
      initialProjection: projection
    });
  };

  const handleAutoTradeChange = () => {
    onResetCreateProjections();
    updateProjection({
      change: {
        ...onAutoTradeInGameChange(autoTrade, inGame),
        projectionValue
      },
      initialProjection: projection
    });
  };

  const handleInGameChange = () => {
    onResetCreateProjections();
    updateProjection({
      change: {
        ...onAutoTradeInGameChange(autoTrade, inGame, false),
        projectionValue
      },
      initialProjection: projection
    });
  };

  const [getAverageScore, { isSuccess, data, isUninitialized }] =
    useGetAverageScoreMutation();

  const onLineScoreChange = (newValue: string) => {
    onResetCreateProjections();
    const newValueNumber = newValue === '' ? newValue : Number(newValue);
    const removeStatus = newValueNumber === 0 || newValue === '';

    if (
      (isUninitialized || (!averageScore && data?.[0]?.score_avg)) &&
      newValueNumber
    ) {
      getAverageScore([
        {
          duration_id: Number(durationId),
          foreign_id: foreignId,
          player_id: Number(playerId),
          stat_type_id: Number(statTypeId)
        }
      ]).catch(() => {});
    }

    const newBoardTime =
      boardTime || DateTime.now().plus({ minutes: 15 }).toISO();

    const newDateError = isTimeAfter({
      dateValue: DateTime.fromISO(newBoardTime),
      dateToCompareAgainst: DateTime.fromISO(startTime)
    });

    const newScoreError = determineScoreError({
      averageScore,
      projectionValue: newValueNumber
    });

    updateProjection({
      change: {
        projectionValue: newValueNumber,
        status: removeStatus ? 'removed' : 'pre_game',
        boardTime: newBoardTime,
        dateError: newDateError,
        scoreError: newScoreError
      },
      initialProjection: projection
    });
  };

  useEffect(() => {
    if (!averageScore && isSuccess && data?.[0]?.score_avg) {
      const newAverageScore = Number(data[0]?.score_avg);

      const newScoreError = determineScoreError({
        averageScore: newAverageScore,
        projectionValue
      });

      if (Object.keys(change).length === 0) {
        return;
      }

      updateProjection({
        change: {
          averageScore: newAverageScore,
          scoreError: newScoreError
        },
        initialProjection: projection
      });
    }
  }, [
    averageScore,
    change,
    data,
    isSuccess,
    projection,
    projectionValue,
    updateProjection
  ]);

  const onStatTypeChange = (newValue: SelectOption) => {
    if (newValue.value === statTypeId) {
      return;
    }

    const { name, value } = newValue;

    getAverageScore([
      {
        duration_id: Number(durationId),
        foreign_id: foreignId,
        player_id: Number(playerId),
        stat_type_id: Number(value)
      }
    ]).catch(() => {});

    updateProjection({
      change: {
        statType: name ?? '',
        statTypeId: value,
        averageScore: undefined,
        scoreError: false
      },
      initialProjection: projection
    });
  };

  const onStatusChange = (newValue: Status) => {
    if (newValue === status) {
      return;
    }

    updateProjection({
      change: {
        status: newValue
      },
      initialProjection: projection
    });
  };

  const isPastStartTime = isTimeBefore({
    dateValue: DateTime.fromISO(startTime)
  });
  const isProjectionValueLocked = isPastStartTime || isPromo;

  const { isActive: isAFXActive } = useSplitFlag({
    flagName: 'AUTO_TRADER_ENABLED'
  });

  const handleTradeAttributeChange = ({
    key,
    value
  }: {
    key: ProbabilityType;
    value: number | undefined;
  }) => {
    updateProjection({
      change: {
        [key]: value
      },
      initialProjection: projection
    });
  };

  return (
    <tr className="border-soFresh-140 border-t">
      {draft && !isCopied ? (
        <td className="py-4">
          <Select
            label="Stat Type Select"
            onChange={onStatTypeChange}
            options={statTypes}
            size="small"
            value={statTypeId}
            zIndex={60}
          />
        </td>
      ) : (
        <StatTypeLabel statType={statType} status={status} />
      )}

      <td className="justify-center py-4">
        <span className="text-soClean-100 text-xs">
          {!draft && projectionId}
        </span>
      </td>

      <td className="justify-center p-4">
        <Input
          className="bg-soFresh-110 text-soClean-100 border-soFresh-140 placeholder:text-soClean-140 block h-8 w-full rounded-md border sm:text-sm sm:leading-6"
          onValueChange={onDescriptionChange}
          value={description}
        />
      </td>

      <PlayerModalProjectionBoardTimePicker
        boardTime={boardTime}
        dateError={dateError}
        disabled={isProjectionValueLocked}
        handleChange={onChangeBoardTime}
        initialBoardTime={projection.boardTime}
        startTime={startTime}
        status={status}
      />

      <PlayerModalProjectionStartTimePicker
        dateError={dateError}
        disabled={isProjectionValueLocked}
        handleChange={onChangeStartTime}
        initialStartTime={projection.startTime}
        startTime={startTime}
        status={status}
      />

      {isDisplayTimeVisible && (
        <DisplayTimePicker
          dateError={dateError}
          disabled={isProjectionValueLocked}
          handleChange={onChangeDisplayTime}
          initialDisplayTime={projection.displayTime}
          displayTime={displayTime}
          status={status}
        />
      )}

      <RankInputCell
        handleProjectionRankChange={onRankChange}
        initialRank={projection.rank}
        projectionId={projectionId}
        rank={rank}
      />

      <TenGameAverage
        statType={statType}
        status={status}
        tenGameAvg={averageScore}
      />

      <ProjectionInputCell
        afx={isAFXActive}
        autoTrade={autoTrade}
        autoTradeDisabled={isProjectionValueLocked}
        disabled={
          isProjectionValueLocked || (isAFXActive && autoTrade && !draft)
        }
        isCommunityPlay={isCommunityPlay}
        inGame={inGame}
        inGameDisabled={isProjectionValueLocked}
        initialValue={projection.projectionValue}
        isInvalidProjectionValue={scoreError}
        isPromo={isPromo}
        oddsType={oddsType}
        onAutoTradeChange={handleAutoTradeChange}
        onInGameChange={handleInGameChange}
        onChange={onLineScoreChange}
        optIn={optIn}
        projectionId={projectionId}
        value={projectionValue}
      />

      <td className="justify-center py-4">
        <span className="text-soClean-100 flex w-10 items-center justify-center text-xs">
          <StatusSelect
            boardTime={boardTime}
            containerClassName="items-center justify-center flex"
            label="status"
            iconOnly
            onChange={onStatusChange}
            value={status}
            zIndex={60}
          />
        </span>
      </td>
      <TradeSettingsProbabilityInput
        disabled={isProjectionValueLocked}
        probabilityType="minProbabilityOfMore"
        onValueChange={handleTradeAttributeChange}
        value={minProbabilityOfMore}
      />
      <TradeSettingsProbabilityInput
        disabled={isProjectionValueLocked}
        probabilityType="targetProbabilityOfMore"
        onValueChange={handleTradeAttributeChange}
        value={targetProbabilityOfMore}
      />
      <TradeSettingsProbabilityInput
        disabled={isProjectionValueLocked}
        probabilityType="maxProbabilityOfMore"
        onValueChange={handleTradeAttributeChange}
        value={maxProbabilityOfMore}
      />
    </tr>
  );
};
